import { t } from "ttag";

import { EXTERNAL_LINK } from "@/config/external-links";
import {
  getMedicationRequestStatusLabel,
  MEDICATION_REQUEST_STATUS,
  MEDICATION_REQUEST_STATUS_COLOR,
} from "@/features/my-meds/models";
import type { MedicationRequest } from "@/features/my-meds/models";
import { MarkMedicationAsReceivedModal } from "@/features/my-meds/sections";
import { CATEGORY_COLOR } from "@/features/products/models";
import { useBoolean } from "@/shared/hooks";
import { Badge, Button, DeliveryIcon } from "@/shared/ui";
import { RequestCard } from ".";
import { getMedRequestDescription } from "./constants";

interface MedicationRequestCardProps {
  medicationRequest: MedicationRequest;
}

export const MedicationRequestCard = ({
  medicationRequest,
}: MedicationRequestCardProps) => {
  const hasDrugInfo = Boolean(
    medicationRequest.drugName ?? medicationRequest.dose,
  );

  const showTracking =
    medicationRequest.trackingNumber &&
    medicationRequest.status === MEDICATION_REQUEST_STATUS.ON_ITS_WAY;

  const {
    value: showMarkAsReceivedModal,
    setTrue: openMarkAsReceivedModal,
    setFalse: closeMarkAsReceivedModal,
  } = useBoolean();

  return (
    <>
      <RequestCard.Root className="@container gap-3">
        <Badge
          color={MEDICATION_REQUEST_STATUS_COLOR[medicationRequest.status]}
          size="small"
          className="absolute -top-1.5 -right-1 min-w-28"
        >
          {getMedicationRequestStatusLabel(medicationRequest.status)}
        </Badge>

        <div className="flex w-fit flex-wrap items-center gap-2 pt-2 sm:pt-0">
          <RequestCard.Title>
            {medicationRequest.product.name}
          </RequestCard.Title>

          <Badge
            color={CATEGORY_COLOR[medicationRequest.product.category.slug]}
            data-theme={medicationRequest.product.category.slug}
            size="x-small"
          >
            {medicationRequest.product.category.name}
          </Badge>
        </div>

        <div className="grid grid-cols-[1fr_auto] items-center">
          <div className="flex flex-col">
            <RequestCard.Title className="col-start-1 font-medium">
              {t`Request made on ${medicationRequest.dateOfRequest}`}
            </RequestCard.Title>

            {hasDrugInfo && (
              <RequestCard.Details className="my-1 @xl:w-fit">
                <div className="flex items-center gap-1">
                  <RequestCard.Label>{t`Drug name`}:</RequestCard.Label>
                  <RequestCard.Description className="text-sm">
                    {medicationRequest.drugName ?? "-"}
                  </RequestCard.Description>
                </div>

                <div className="hidden h-2.5 w-[1.5px] self-center md:block" />

                <div className="flex items-center gap-1">
                  <RequestCard.Label>{t`Dose`}:</RequestCard.Label>
                  <RequestCard.Description className="text-sm">
                    {medicationRequest.dose ?? "-"}
                  </RequestCard.Description>
                </div>

                <div className="hidden h-2.5 w-[1.5px] self-center md:block" />

                <div className="flex items-center gap-1">
                  <RequestCard.Label>{t`Supply`}:</RequestCard.Label>
                  <RequestCard.Description className="text-sm">
                    {medicationRequest.supply ?? "-"}
                  </RequestCard.Description>
                </div>
              </RequestCard.Details>
            )}

            <RequestCard.Description>
              {getMedRequestDescription(medicationRequest.status)}
            </RequestCard.Description>
          </div>

          {medicationRequest.canMarkAsComplete && (
            <Button
              size="lg"
              className="my-1 hidden @xl:block"
              onClick={openMarkAsReceivedModal}
            >
              {t`Mark as received`}
            </Button>
          )}
        </div>

        {showTracking && (
          <RequestCard.Link
            href={`${EXTERNAL_LINK.TRACKING}${medicationRequest.trackingNumber}`}
          >
            <DeliveryIcon className="size-4 translate-y-0.5" />
            {t`Tracking #${medicationRequest.trackingNumber}`}
          </RequestCard.Link>
        )}

        {medicationRequest.canMarkAsComplete && (
          <Button
            size="lg"
            className="@xl:hidden"
            onClick={openMarkAsReceivedModal}
          >
            {t`Mark as received`}
          </Button>
        )}
      </RequestCard.Root>

      <MarkMedicationAsReceivedModal
        show={showMarkAsReceivedModal}
        onClose={closeMarkAsReceivedModal}
        medicationRequestId={medicationRequest.id}
      />
    </>
  );
};
